// components/tabs/StageTab.jsx
import { useState, useEffect } from 'react';
import { exportSTD215, exportSTD213 } from '../../utils/pdfExport';
import { exportXFDF, exportSTD213XFDF } from '../../utils/xfdfExport';
import { generateSTD215 } from '../../utils/std215Generator';
import { Field, Select, Callout, SectionLabel, EmptyState, Icons } from '../UI';
import { STAGES, PROC_METHODS, fmtDate, fmtMoney, dgsCheck, LPA_METHODS, LPA_SKIPPED_STAGES } from '../../utils/constants';
import { supabase } from '../../utils/supabaseClient';
import SOWBriefingPanel from '../SOWBriefingPanel';


export default function StageTab({
  contract, updateC, updateS215, s215, profile, paal, dgsResult, isAuto,
  applyDefaults, overwriteDefaults, setPendingStage, setShowStageModal, cms = [], toast,
  apiKey, callAI,
}) {
  const [expandedS215, setExpandedS215] = useState(false);
  const [historyExpanded, setHistoryExpanded] = useState(false);
  const [collapsedCM, setCollapsedCM] = useState(true);
  const [collapsedSTD215, setCollapsedSTD215] = useState(true);
  const [cmBriefExpanded, setCmBriefExpanded] = useState(false);
  const hasCmBriefing = !!contract.pennyDrafts?.sowBriefing;
  const [collapsedContractor, setCollapsedContractor] = useState(false);
  const [showXfdfGuide, setShowXfdfGuide] = useState(false);
  const [showXfdf213Guide, setShowXfdf213Guide] = useState(false);
  const [pendingProcMethod, setPendingProcMethod] = useState(null); // method waiting for confirmation

  function stageClick(i) {
    setPendingStage(i);
    setShowStageModal(true);
  }

  // Active CMs only (plus currently assigned one so it's never lost)
  const activeCms = cms.filter(cm => cm.active !== false || cm.id === contract.assignedCmId);

  // Load vendor library for contractor selection
  const [vendors, setVendors] = useState([]);
  useEffect(() => {
    async function loadVendors() {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;
      const { data: prof } = await supabase.from('user_profiles').select('agency_id').eq('id', user.id).single();
      const { data } = await supabase.from('vendor_library').select('*').eq('agency_id', prof?.agency_id).eq('active', true).order('company_name');
      if (data) setVendors(data);
    }
    loadVendors();
  }, []);

  async function createVendorFromContract() {
    if (!s215.contractorName?.trim()) return;
    // Check if already exists
    const existing = vendors.find(v => v.company_name.toLowerCase() === s215.contractorName.toLowerCase());
    if (existing) {
      updateC({ contractorVendorId: existing.id });
      toast.success('Linked to existing vendor: ' + existing.company_name);
      return;
    }
    try {
      const { data: { user } } = await supabase.auth.getUser();
      const { data: prof } = await supabase.from('user_profiles').select('agency_id').eq('id', user.id).single();
      const mainAddr = s215.contractorAddress ? [{
        label: 'Main Office',
        street: s215.contractorAddress || '',
        city: s215.contractorCity || '',
        state: s215.contractorState || 'CA',
        zip: s215.contractorZip || '',
      }] : [];
      const payload = {
        agency_id:    prof?.agency_id,
        company_name: s215.contractorName.trim(),
        phone:        s215.contractorPhone || null,
        email:        s215.contractorEmail || null,
        poc_name:     s215.contractorContactName || null,
        poc_title:    s215.contractorContactTitle || null,
        poc_phone:    s215.contractorContactPhone || null,
        poc_email:    s215.contractorContactEmail || null,
        addresses:    mainAddr,
        active:       true,
        vendor_type:  'agency_managed',
        docs: {}, doc_metadata: {},
      };
      const { data, error } = await supabase.from('vendor_library').insert(payload).select().single();
      if (error) throw error;
      setVendors(p => [...p, data].sort((a, b) => a.company_name.localeCompare(b.company_name)));
      updateC({ contractorVendorId: data.id });
      toast.success('✦ Penny created vendor profile for ' + s215.contractorName + ' — visit Vendor Library to add documents and certifications.');
    } catch (e) {
      toast.error('Could not create vendor profile: ' + e.message);
    }
  }

  function applyVendor(vendorId) {
    const v = vendors.find(v => v.id === vendorId);
    if (!v) return;
    const mainAddr = v.addresses?.[0] || {};
    // Build SB/DVBE cert string — combine if vendor has both
    const certNum = v.sb_cert_number || v.dvbe_cert_number || '';
    updateC({
      contractorVendorId: v.id,
      sbDvbeCertNum: certNum || undefined,
      s215: {
        ...s215,
        contractorName: v.company_name || '',
        contractorTaxId: v.tax_id || '',
        contractorAddress: mainAddr.street || v.address || '',
        contractorCity: mainAddr.city || v.city || '',
        contractorState: mainAddr.state || v.state || 'CA',
        contractorZip: mainAddr.zip || v.zip || '',
        contractorPhone: v.phone || '',
        contractorEmail: v.email || '',
        contractorContactName: v.poc_name || '',
        contractorContactTitle: v.poc_title || '',
        contractorContactPhone: v.poc_phone || '',
        contractorContactEmail: v.poc_email || '',
      }
    });
    toast.success(v.company_name + ' applied from Vendor Library');
  }

  function assignCM(cmId) {
    // Save immediately — don't debounce critical assignment fields
    updateC({ assignedCmId: cmId || null }, true);
  }

  const assignedCm = contract.assignedCmId ? cms.find(c => c.id === contract.assignedCmId) : null;

  return (
    <div>
      {/* ── XFDF Import Instructions Modal ───────────────────────── */}
      {showXfdfGuide && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 24 }}>
          <div style={{ background: 'var(--white)', borderRadius: 'var(--r-lg)', padding: '28px 32px', maxWidth: 520, width: '100%', boxShadow: '0 24px 64px rgba(0,0,0,0.2)' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
              <span style={{ fontSize: 28 }}>📄</span>
              <div>
                <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--navy-900)' }}>Your STD 215 data file has been downloaded</div>
                <div style={{ fontSize: 12, color: 'var(--slate-500)' }}>Follow these steps to fill the official DGS form</div>
              </div>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 20 }}>
              {[
                ["1", "Download the official STD 215 form", "Download directly from documents.dgs.ca.gov — the link is in the button below. Or use your agency copy if you already have it."],
                ['2', 'Open it in Adobe Acrobat or Reader', 'Do not open in a browser — it must be opened in the full Adobe application for XFA forms to work.'],
                ['3', 'Import the data file', 'In Acrobat: go to Tools → Prepare Form → More → Import Data (or Edit → Import Data). Select the .xfdf file you just downloaded.'],
                ['4', 'Review and complete', 'All fields GoldenScope knows will be pre-filled. Review for accuracy, complete any remaining fields (signatures, checkboxes), and save.'],
                ["5", "Add signatures and route", "Print for wet signatures or use Adobe e-signature tools, then route per your agency process."],
              ].map(([num, title, desc]) => (
                <div key={num} style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
                  <div style={{ width: 24, height: 24, borderRadius: '50%', background: 'var(--navy-800)', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 700, flexShrink: 0, marginTop: 1 }}>{num}</div>
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--navy-800)', marginBottom: 2 }}>{title}</div>
                    <div style={{ fontSize: 12, color: 'var(--slate-500)', lineHeight: 1.5 }}>{desc}</div>
                  </div>
                </div>
              ))}
            </div>

            <div style={{ padding: '10px 14px', background: 'var(--blue-50)', border: '1px solid var(--blue-100)', borderRadius: 'var(--r-md)', fontSize: 12, color: 'var(--blue-700)', marginBottom: 18 }}>
              💡 <strong>Tip:</strong> The downloaded file is named <code style={{ background: 'var(--blue-100)', padding: '1px 5px', borderRadius: 4 }}>{(contract.contractNum || s215.agreementNum || 'STD215')}_data.xfdf</code> — keep it with your contract package.
            </div>

            <div style={{ display: 'flex', gap: 10 }}>
              <a href="https://www.documents.dgs.ca.gov/dgs/fmc/pdf/std215.pdf" target="_blank" rel="noreferrer" className="btn btn-gold" style={{ flex: 1, justifyContent: 'center', textDecoration: 'none' }}>
                Get Official STD 215 Form →
              </a>
              <button className="btn btn-ghost" onClick={() => setShowXfdfGuide(false)} style={{ flex: 1 }}>
                Done
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── STD 213 XFDF Import Instructions Modal ─────────────── */}
      {showXfdf213Guide && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 24 }}>
          <div style={{ background: 'var(--white)', borderRadius: 'var(--r-lg)', padding: '28px 32px', maxWidth: 520, width: '100%', boxShadow: '0 24px 64px rgba(0,0,0,0.2)' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
              <span style={{ fontSize: 28 }}>📄</span>
              <div>
                <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--navy-900)' }}>Your STD 213 data file has been downloaded</div>
                <div style={{ fontSize: 12, color: 'var(--slate-500)' }}>Follow these steps to fill the official DGS form</div>
              </div>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 20 }}>
              {[
                ["1", "Download the official STD 213 form", "Download directly from documents.dgs.ca.gov — the link is in the button below. Or use your agency copy if you already have it."],
                ["2", "Open it in Adobe Acrobat or Reader", "Do not open in a browser — it must be opened in the full Adobe application for XFA forms to work."],
                ["3", "Import the data file", "In Acrobat: go to Tools, then Prepare Form, then Import Data (or Edit, then Import Data). Select the .xfdf file you just downloaded."],
                ["4", "Review and complete", "Contract info, parties, exhibits, and dates will be pre-filled. Add signatures and any remaining fields."],
                ["5", "Route with the STD 215", "The STD 213 and STD 215 are submitted together as your contract package to DGS/OLS."],
              ].map(([num, title, desc]) => (
                <div key={num} style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
                  <div style={{ width: 24, height: 24, borderRadius: '50%', background: 'var(--navy-800)', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 700, flexShrink: 0, marginTop: 1 }}>{num}</div>
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--navy-800)', marginBottom: 2 }}>{title}</div>
                    <div style={{ fontSize: 12, color: 'var(--slate-500)', lineHeight: 1.5 }}>{desc}</div>
                  </div>
                </div>
              ))}
            </div>
            <div style={{ padding: '10px 14px', background: 'var(--blue-50)', border: '1px solid var(--blue-100)', borderRadius: 'var(--r-md)', fontSize: 12, color: 'var(--blue-700)', marginBottom: 18 }}>
              💡 <strong>Note:</strong> Exhibits A, B, and C are pre-populated with standard titles. Review and adjust page counts before routing.
            </div>
            <div style={{ display: 'flex', gap: 10 }}>
              <a href="http://www.documents.dgs.ca.gov/dgs/fmc/pdf/std213.pdf" target="_blank" rel="noreferrer" className="btn btn-gold" style={{ flex: 1, justifyContent: 'center', textDecoration: 'none' }}>
                Get Official STD 213 Form →
              </a>
              <button className="btn btn-ghost" onClick={() => setShowXfdf213Guide(false)} style={{ flex: 1 }}>Done</button>
            </div>
          </div>
        </div>
      )}

      {/* ── Procurement Method Change Confirmation ───────────────── */}
      {pendingProcMethod && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 24 }}>
          <div style={{ background: 'var(--white)', borderRadius: 'var(--r-lg)', padding: '28px 32px', maxWidth: 480, width: '100%', boxShadow: '0 24px 64px rgba(0,0,0,0.2)' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
              <div style={{ width: 36, height: 36, borderRadius: 8, background: 'var(--navy-900)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18 }}>✒</div>
              <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--navy-800)' }}>Penny — Procurement Method Change</div>
            </div>
            <p style={{ fontSize: 13.5, color: 'var(--slate-600)', lineHeight: 1.7, marginBottom: 8 }}>
              You're switching from <strong>{contract.procMethod || 'the current method'}</strong> to <strong>{pendingProcMethod}</strong>.
            </p>
            <p style={{ fontSize: 13.5, color: 'var(--slate-600)', lineHeight: 1.7, marginBottom: 16 }}>
              I have an existing SCM Compliance Brief cached for <strong>{contract.procMethod}</strong>. If you proceed, I'll generate a new brief for <strong>{pendingProcMethod}</strong> — your existing brief will be kept and both will be available.
            </p>
            <div style={{ padding: '12px 16px', background: 'var(--blue-50)', borderRadius: 'var(--r-md)', marginBottom: 20, fontSize: 12.5, color: 'var(--blue-700)' }}>
              ℹ Penny will generate the new SCM brief for {pendingProcMethod} in the background — you can keep working while it runs. A notification will appear when it's ready.
            </div>
            <div style={{ display: 'flex', gap: 10 }}>
              <button className="btn btn-gold" style={{ flex: 1 }} onClick={() => {
                const newMethod = pendingProcMethod;
                setPendingProcMethod(null);
                updateC({
                  procMethod: newMethod,
                  pennyDrafts: { ...(contract.pennyDrafts || {}), triggerScmRefresh: newMethod + '_' + (contract.isIT || 'no') + '_' + Date.now() }
                });
                toast && toast.info('✒ Penny is generating the new SCM brief for ' + newMethod + ' in the background…');
              }}>
                ✓ Yes, switch to {pendingProcMethod}
              </button>
              <button className="btn btn-ghost" style={{ flex: 1 }} onClick={() => setPendingProcMethod(null)}>
                Cancel — keep {contract.procMethod || 'current method'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Stage Pipeline ──────────────────────────────────────── */}
      <div className="card">
        <div className="card-title-row">
          <h3 className="card-title" style={{ marginBottom: 0 }}>Stage Pipeline</h3>
          <div style={{ display: 'flex', gap: 8 }}>
            {contract.stage > 0 && (
              <button className="btn btn-ghost btn-sm" onClick={() => stageClick(contract.stage - 1)}>
                ← Back: {STAGES[contract.stage - 1]?.short}
              </button>
            )}
            {contract.stage < STAGES.length - 1 && (
              <button className="btn btn-gold btn-sm" onClick={() => stageClick(contract.stage + 1)}>
                Advance: {STAGES[contract.stage + 1]?.short} →
              </button>
            )}
          </div>
        </div>

        <div className="stage-pipeline" style={{ marginTop: 16 }}>
          {STAGES.map((s, i) => {
            const isLPAContract = LPA_METHODS.has(contract.procMethod);
            const skipped = isLPAContract && LPA_SKIPPED_STAGES.has(i);
            const state = i < contract.stage ? 'done' : i === contract.stage ? 'current' : 'future';
            return (
              <div key={i} className={`stage-step ${state}`} style={skipped ? { opacity: 0.35 } : {}}>
                <div className="stage-dot-wrap" onClick={() => !skipped && stageClick(i)} title={skipped ? s.label + ' — N/A for LPA' : s.label} style={{ cursor: skipped ? 'default' : 'pointer' }}>
                  <div className={`stage-dot ${state}`} style={skipped ? { textDecoration: 'line-through', fontSize: 9 } : {}}>
                    {skipped ? '—' : state === 'done' ? '✓' : i + 1}
                  </div>
                  <div className={`stage-label ${state}`}>{s.short}</div>
                </div>
              </div>
            );
          })}
        </div>

        {/* ── Penny Stage Briefing ── */}
        {(() => {
        const stage = contract.stage || 0;
        const isIT = contract.isIT === 'yes';
        const isLPAContract = LPA_METHODS.has(contract.procMethod);
        const isCMASContract = isLPAContract; // alias for backward compat
        const lpaLabel = contract.procMethod === 'CALNET' ? 'CALNET' : contract.procMethod === 'SLP' ? 'SLP' : contract.procMethod === 'LPA-Other' ? 'LPA' : 'CMAS';
        const budget = parseFloat(String(contract.budget || '0').replace(/[$,]/g, '')) || 0;
        const paalThreshold = isIT ? (paal?.it || 200000) : (paal?.nonIT || 50000);
        const abovePaal = budget > 0 && budget > paalThreshold;
        const hasCm = !!contract.assignedCmId;
        const hasSow = !!(contract.pennyDrafts?.exAFinal || contract.pennyDrafts?.sow);
        const hasExB = !!contract.pennyDrafts?.exBFinal;
        const hasExD = !!contract.pennyDrafts?.exDFinal;
        const hasCmasDoc = !!contract.pennyDrafts?.cmasDoc;
        const hasCmasBrief = !!contract.pennyDrafts?.cmasContext?.brief;
        const cmasQuotes = contract.pennyDrafts?.cmasQuotes || [];
        const cmasQuoteCount = cmasQuotes.filter(q => q.vendor?.trim()).length;
        const procMethod = contract.procMethod;

        const BRIEFINGS = [
          {
            desc: "This is where the contract originates. The goal at this stage is to capture the business need, confirm a funding source, and assign a contract manager so the work can begin.",
            focus: ["Document the business justification clearly — it will flow into Exhibit A", "Confirm the funding source and appropriation before proceeding", "Assign a contract manager from the CM Roster"],
          },
          {
            desc: "Based on the contract type and budget, you'll select the procurement method that governs how this contract is solicited, evaluated, and awarded.",
            focus: [
              isIT ? "This is an IT contract — procurement methods follow SCM Vol 2" : "This is a Non-IT contract — procurement methods follow SCM Vol 1",
              budget > 0 ? `Budget is ${fmtMoney(budget)} — ${abovePaal ? `above the $${fmtMoney(paalThreshold)} PAAL threshold, DGS routing will be required` : `within the $${fmtMoney(paalThreshold)} PAAL threshold`}` : "Enter the contract budget to see PAAL threshold guidance",
              "Document the rationale for your method selection in writing",
            ],
          },
          {
            desc: isCMASContract
              ? (lpaLabel + " orders still require a complete Scope of Work — Penny will draft it for you. Exhibit B and D are not typically needed; the vendor's price list and master agreement terms cover those. Upload the master agreement in the Penny tab so Penny can guide the drafting.")
              : "This is the core drafting stage. Penny builds your Scope of Work, Exhibit B, and Exhibit D — and can run a pre-review package check before anything goes to legal.",
            focus: isCMASContract ? [
              !hasCmasDoc ? ('Upload the master agreement document in the Penny tab — Penny needs it to guide the ' + lpaLabel + ' order') : hasCmasBrief ? ('✓ Master agreement uploaded and analyzed by Penny') : ('Master agreement uploaded — run Analyze Document in the Penny tab to generate Penny\'s brief'),
              !hasSow ? "Exhibit A (SOW) not yet drafted — open the Penny tab to begin" : "✓ Exhibit A drafted",
              ("Exhibit B and Exhibit D are not typically required for " + lpaLabel + " orders — the vendor's price list and master agreement terms apply"),
              "Run a Pre-Review Package Check in the Penny tab before advancing",
            ] : [
              !hasSow ? "Exhibit A (SOW) not yet drafted — open the Penny tab to begin" : "✓ Exhibit A drafted",
              !hasExB ? "Exhibit B (Budget Detail) not yet drafted" : "✓ Exhibit B drafted",
              !hasExD ? "Exhibit D (Special Terms) not yet drafted" : "✓ Exhibit D drafted",
              "Run a Pre-Review Package Check in the Penny tab before advancing to Internal Review",
            ],
          },
          {
            desc: "The STD 215 is the official DGS approval and routing form. It must be fully completed before this contract can advance to review or be submitted to DGS/OLS.",
            focus: [
              "Complete all fields in the STD 215 tab — contractor details, budget, and signatory authority",
              "Confirm the DGS Agreement Number matches your contract number",
              abovePaal ? `Budget is above the $${fmtMoney(paalThreshold)} PAAL threshold — DGS/OLS routing will be required at execution` : `Budget is within the $${fmtMoney(paalThreshold)} PAAL threshold — no DGS routing required unless otherwise mandated`,
            ],
          },
          {
            desc: "The contract package goes to division management and legal counsel for concurrent review. All open comments and compliance flags should be resolved before advancing.",
            focus: [
              "Use the Sharing tab to submit the review package and notify reviewers",
              "All Penny pre-review flags should be resolved or documented",
              "Legal and supervisor must both approve before posting to CalEProcure",
            ],
          },
          {
            desc: isCMASContract
              ? (lpaLabel + " orders do not require a public solicitation posting. This stage is not applicable — advance past it.")
              : "The solicitation is live on CalEProcure. Your primary responsibility is to monitor for vendor questions and issue addenda promptly.",
            focus: isCMASContract ? [
              ('This stage is not applicable to ' + lpaLabel + ' — no CalEProcure posting required'),
              "Advance to the next stage",
            ] : [
              "Monitor CalEProcure for bidder questions — respond or issue addenda within 5 business days",
              "All addenda must be posted publicly before the bid close date",
              "Maintain confidentiality of respondents during the bid period",
            ],
          },
          {
            desc: isCMASContract
              ? (lpaLabel + " orders do not have a public bid period. This stage is not applicable — advance past it.")
              : "The bid period is open. Do not communicate with individual bidders about substantive matters — all responses must go through formal addenda.",
            focus: isCMASContract ? [
              ('This stage is not applicable to ' + lpaLabel + ' — no bid period required'),
              "Advance to the next stage",
            ] : [
              "Log all questions received in the Emails tab",
              "Issue addenda for any clarifications that affect scope, specs, or evaluation criteria",
              "Confirm the bid close date and ensures it allows adequate response time",
            ],
          },
          {
            desc: isCMASContract
              ? ('SB and DVBE participation goals still apply to ' + lpaLabel + ' orders, but the process is different — you document vendor quote outreach rather than a formal solicitation. At least one SB or DVBE vendor quote is strongly encouraged.')
              : "Before award, California law requires documentation of Small Business and DVBE participation efforts. This must be completed in writing before advancing.",
            focus: isCMASContract ? [
              cmasQuoteCount < 3 ? `Only ${cmasQuoteCount} vendor quote(s) entered — at least 3 required. Log all quotes in the Penny tab CMAS Quote Comparison card` : `✓ ${cmasQuoteCount} vendor quotes documented`,
              cmasQuotes.some(q => q.sb) ? "✓ At least one SB vendor quoted" : ('Prioritize quoting certified Small Business ' + lpaLabel + ' vendors where available'),
              cmasQuotes.some(q => q.dvbe) ? "✓ At least one DVBE vendor quoted" : ('Document DVBE outreach — note if no DVBE vendors hold the applicable ' + lpaLabel + ' schedule'),
              "Document price reasonableness in the Quote Comparison card in the Penny tab",
            ] : [
              "Document solicitation method, SB participation goal, and DVBE requirement in the SB/DVBE panel below",
              "Record outreach efforts and any certified SB/DVBE firms that responded",
              procMethod === 'DVBE Option' ? "DVBE Option: at least 3 certified DVBE firms must be contacted" : "Standard SB goal is 25% of contract value unless waived",
            ],
          },
          {
            desc: isCMASContract
              ? (lpaLabel + " orders do not require formal bid evaluation scoring. This stage is not applicable — vendor selection is based on the quote comparison and price reasonableness determination documented in the Penny tab.")
              : "Evaluate bids against the pre-established evaluation criteria. Scoring must be documented and defensible. Do not deviate from published criteria.",
            focus: isCMASContract ? [
              ('This stage is not applicable to ' + lpaLabel + ' — no formal evaluation matrix required'),
              ('Selection is documented in the Quote Comparison card in the Penny tab'),
              "Advance to Award Decision",
            ] : [
              "Evaluation must follow the criteria published in the solicitation — no deviations",
              "Document each evaluator's scores independently before discussion",
              "Use the Vendor Verification tab to confirm required certifications for the apparent awardee",
            ],
          },
          {
            desc: "You've selected an awardee. Before notifying them, confirm the award decision is documented, the selection rationale is written, and all required approvals are obtained.",
            focus: [
              "Prepare written award notification — do not notify verbally first",
              "Document the selection rationale referencing the evaluation scores",
              "Confirm no protest grounds exist before issuing notification",
            ],
          },
          {
            desc: "California Government Code §19131 requires notification to the exclusive employee representative before a personal services contract is executed. A 10-day waiting period applies.",
            focus: [
              "Send written notification to the exclusive representative (union) via certified mail",
              "The 10-day waiting period begins on the date of receipt",
              "Document the notification date and method in your procurement file",
            ],
          },
          {
            desc: "Any losing bidder may file a protest within 5 business days of award notification. Do not execute the contract until the protest period has expired with no protests filed.",
            focus: [
              "Track the protest deadline — 5 business days from the date of award notification",
              "If a protest is filed, do not proceed to execution until it is formally resolved",
              "Document the protest period close date in your contract file",
            ],
          },
          {
            desc: "Both parties sign the contract. No work may begin and no invoices may be submitted until all required signatures are obtained and the contract is fully executed.",
            focus: [
              "Both parties must sign before any work begins — this is a hard requirement",
              !hasCm ? "No contract manager assigned — assign one in the Stage tab before execution" : `Contract manager: ${cms.find(c => c.id === contract.assignedCmId)?.name || 'assigned'}`,
              abovePaal ? `Budget is above PAAL — DGS/OLS routing is required before execution is complete` : "Confirm signatory authority per your agency's delegation matrix",
            ],
          },
          {
            desc: "If the contract is above the PAAL threshold, the fully executed package must be routed to DGS/OLS for review and approval before work may proceed.",
            focus: [
              abovePaal
                ? `This contract at ${fmtMoney(budget)} exceeds the $${fmtMoney(paalThreshold)} PAAL threshold — DGS/OLS routing is required`
                : `This contract is within the $${fmtMoney(paalThreshold)} PAAL threshold — DGS/OLS routing is not required unless your agency mandates it`,
              "Submit the complete package: STD 213, STD 215, all exhibits, and SB/DVBE documentation",
              "Use the Assembly tab to export the full contract package for submission",
            ],
          },
          {
            desc: "The contract is executed and work is underway. Complete the handoff to the contract manager and ensure all documentation is finalized and archived.",
            focus: [
              "Complete the CM handoff package — scope, deliverables, deadlines, and payment terms",
              "File the fully executed contract with all exhibits in the Vault",
              "Send the post-award contractor survey if required by your agency",
            ],
          },
        ];

        const briefing = BRIEFINGS[stage];
        if (!briefing) return null;

        const currentStage = STAGES[stage];

        return (
          <div style={{ marginTop: 16, borderTop: '1px solid var(--slate-100)', paddingTop: 14 }}>
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, marginBottom: 10 }}>
              <div style={{ width: 26, height: 26, borderRadius: 6, background: 'var(--navy-800)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, flexShrink: 0 }}>✒</div>
              <div style={{ flex: 1 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 3 }}>
                  <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--gold-600)', textTransform: 'uppercase', letterSpacing: '.06em' }}>Penny</span>
                  <span style={{ fontSize: 11, color: 'var(--slate-400)' }}>Stage {stage + 1} — {currentStage?.label}</span>
                </div>
                <p style={{ fontSize: 12.5, color: 'var(--slate-700)', lineHeight: 1.65, margin: 0 }}>{briefing.desc}</p>
              </div>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 5, paddingLeft: 36 }}>
              {briefing.focus.map((item, i) => {
                const isDone = item.startsWith('✓');
                const isWarning = item.toLowerCase().includes('not yet') || item.toLowerCase().includes('required') || item.toLowerCase().includes('must') || item.toLowerCase().includes('above paal') || item.toLowerCase().includes('no contract manager');
                const color = isDone ? 'var(--green-700)' : isWarning ? 'var(--orange-700)' : 'var(--navy-700)';
                const dotColor = isDone ? 'var(--green-500)' : isWarning ? 'var(--orange-400)' : 'var(--gold-400)';
                return (
                  <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: 7, fontSize: 12 }}>
                    <div style={{ width: 5, height: 5, borderRadius: '50%', background: dotColor, flexShrink: 0, marginTop: 5 }} />
                    <span style={{ color, lineHeight: 1.55 }}>{item}</span>
                  </div>
                );
              })}
            </div>
          </div>
        );
      })()}

        {(contract.stageHistory || []).length > 1 && (() => {
          const history = [...(contract.stageHistory || [])].reverse();
          const mostRecent = history[0];
          const rest = history.slice(1);
          return (
            <div style={{ marginTop: 16, borderTop: '1px solid var(--slate-100)', paddingTop: 12 }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.06em', color: 'var(--slate-400)' }}>
                  Stage History
                </div>
                {rest.length > 0 && (
                  <button onClick={() => setHistoryExpanded(p => !p)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 11, color: 'var(--slate-400)', display: 'flex', alignItems: 'center', gap: 4, padding: 0 }}>
                    {historyExpanded ? 'Show less' : `+${rest.length} more`}
                    <span style={{ fontSize: 10, transition: 'transform .2s', transform: historyExpanded ? 'rotate(0deg)' : 'rotate(-90deg)', display: 'inline-block' }}>▼</span>
                  </button>
                )}
              </div>
              {/* Most recent entry — always visible */}
              <div style={{ display: 'flex', gap: 12, fontSize: 12, alignItems: 'baseline', padding: '3px 0' }}>
                <span style={{ color: 'var(--slate-400)', minWidth: 90, fontFamily: 'var(--font-mono)', fontSize: 11 }}>{fmtDate(mostRecent.date)}</span>
                <span style={{ fontWeight: 600, color: 'var(--navy-600)', minWidth: 150 }}>{STAGES[mostRecent.stage]?.label}</span>
                {mostRecent.note && mostRecent.note !== STAGES[mostRecent.stage]?.label && (
                  <span style={{ color: 'var(--slate-500)' }}>{mostRecent.note}</span>
                )}
              </div>
              {/* Remaining entries — visible when expanded */}
              {historyExpanded && rest.map((h, i) => (
                <div key={i} style={{ display: 'flex', gap: 12, fontSize: 12, alignItems: 'baseline', padding: '3px 0', opacity: 0.8 }}>
                  <span style={{ color: 'var(--slate-400)', minWidth: 90, fontFamily: 'var(--font-mono)', fontSize: 11 }}>{fmtDate(h.date)}</span>
                  <span style={{ fontWeight: 600, color: 'var(--navy-600)', minWidth: 150 }}>{STAGES[h.stage]?.label}</span>
                  {h.note && h.note !== STAGES[h.stage]?.label && (
                    <span style={{ color: 'var(--slate-500)' }}>{h.note}</span>
                  )}
                </div>
              ))}
            </div>
          );
        })()}
      </div>

      {/* ── Contract Details ────────────────────────────────────── */}
      <div className="card">
        <h3 className="card-title">Contract Details</h3>
        <div className="form-grid">
          <Field label="Contract Title" value={contract.title} onChange={v => updateC({ title: v })} required className="span-full" />
          <Field label="Energy Safety Contract No." value={contract.contractNum || ''} onChange={v => { updateC({ contractNum: v }); updateS215('agreementNum', v); }} placeholder="e.g., ES-2026-001" monospace hint="Also used as DGS Agreement No." />
          <Field label="DGS Agreement No." value={s215.agreementNum} onChange={v => updateS215('agreementNum', v)} monospace hint="Auto-synced from contract number — override only if DGS assigns a different number" />
          <Field label="Description" value={contract.description} onChange={v => updateC({ description: v })} rows={3} className="span-full" />
          <Field label="Budget ($)" value={String(contract.budget || "").replace(/[$,]/g, "")} onChange={v => updateC({ budget: v })} type="number" />
          <Field label="Category" value={contract.category} onChange={v => updateC({ category: v })} />
          <Select label="Contract Type" value={contract.isIT} onChange={v => updateC({ isIT: v })}>
            <option value="no">Non-IT — SCM Vol 1</option>
            <option value="yes">IT — SCM Vol 2</option>
          </Select>
          <Select label="Procurement Method" value={contract.procMethod} onChange={v => {
            if (!v || v === contract.procMethod) return;
            const cacheKey = (contract.procMethod || 'unspecified') + '_' + (contract.isIT || 'no');
            const hasCachedBrief = contract.pennyDrafts?.scmContext?.[cacheKey];
            if (hasCachedBrief) {
              // Prompt confirmation before switching — a new cache will be needed
              setPendingProcMethod(v);
            } else {
              updateC({ procMethod: v });
            }
          }}>
            <option value="">Select…</option>
            {PROC_METHODS.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
          </Select>
          <Field label="Internal Notes" value={contract.notes} onChange={v => updateC({ notes: v })} rows={2} className="span-full" />
        </div>
      </div>

      {/* ── Procurement Authority & Exemptions ────────────────── */}
      <div className="card" style={{
        borderLeft: contract.exemption?.active
          ? '3px solid var(--green-500)'
          : dgsResult?.required ? '3px solid var(--red-500)' : '3px solid var(--green-500)',
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 16, flexWrap: 'wrap' }}>
          <div style={{ flex: 1 }}>
            {/* Exemption active — show green exempt badge, hide DGS flag */}
            {contract.exemption?.active ? (
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                <span style={{
                  display: 'inline-flex', alignItems: 'center', gap: 6,
                  padding: '4px 12px', borderRadius: 'var(--r-full)',
                  background: 'var(--green-50)', border: '1.5px solid var(--green-100)',
                  color: 'var(--green-600)', fontWeight: 700, fontSize: 12,
                }}>
                  ✓ Statutory Exemption Active
                </span>
                {contract.exemption.statute && (
                  <span style={{ fontSize: 12.5, color: 'var(--slate-600)' }}>
                    {contract.exemption.statute}
                    {contract.exemption.effectiveDate ? ' · Effective ' + contract.exemption.effectiveDate : ''}
                  </span>
                )}
                {contract.exemption.description && (
                  <div style={{ width: '100%', fontSize: 12, color: 'var(--slate-500)', marginTop: 4 }}>
                    {contract.exemption.description}
                  </div>
                )}
              </div>
            ) : dgsResult ? (
              <div style={{ marginTop: 4 }}>
                {(dgsResult.otaRequired || dgsResult.otaExempted) && dgsResult.olsRequired ? (
                  <div style={{ padding: '10px 14px', borderRadius: 'var(--r-md)', background: dgsResult.otaExempted ? 'var(--teal-50)' : 'var(--red-50)', border: '1.5px solid ' + (dgsResult.otaExempted ? 'var(--teal-200)' : 'var(--red-100)') }}>
                    <div style={{ fontWeight: 700, fontSize: 12, color: dgsResult.otaExempted ? 'var(--teal-700)' : 'var(--red-700)', marginBottom: 6 }}>
                      {dgsResult.otaExempted ? '✓ OTA Exempted — DGS/OLS Still Required' : '⚠ DGS Involvement Required — Two Touchpoints'}
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
                      <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
                        <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 7px', borderRadius: 99, background: dgsResult.otaExempted ? 'var(--teal-100)' : 'var(--red-100)', color: dgsResult.otaExempted ? 'var(--teal-700)' : 'var(--red-700)', whiteSpace: 'nowrap', marginTop: 1 }}>DGS/OTA</span>
                        <span style={{ fontSize: 11.5, color: 'var(--slate-600)' }}>
                          {dgsResult.otaExempted
                            ? 'Exempted — ' + (contract.otaExemption?.basis || '⚠ Select exemption basis below') + '. Agency may conduct this solicitation directly.'
                            : 'Must conduct the competitive solicitation on your behalf before you can release it. Submit a requisition to OTA first.'}
                        </span>
                      </div>
                      <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
                        <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 7px', borderRadius: 99, background: 'var(--orange-100)', color: 'var(--orange-700)', whiteSpace: 'nowrap', marginTop: 1 }}>DGS/OLS</span>
                        <span style={{ fontSize: 11.5, color: 'var(--slate-600)' }}>Must review and approve the contract before execution.</span>
                      </div>
                    </div>
                  </div>
                ) : dgsResult.olsRequired ? (
                  // OLS only
                  <div style={{ padding: '10px 14px', borderRadius: 'var(--r-md)', background: 'var(--orange-50)', border: '1.5px solid var(--orange-100)' }}>
                    <div style={{ fontWeight: 700, fontSize: 12, color: 'var(--orange-700)', marginBottom: 4 }}>⚠ DGS/OLS Approval Required</div>
                    <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
                      <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 7px', borderRadius: 99, background: 'var(--orange-100)', color: 'var(--orange-700)', whiteSpace: 'nowrap', marginTop: 1 }}>DGS/OLS</span>
                      <span style={{ fontSize: 11.5, color: 'var(--slate-600)' }}>Contract must be approved by DGS/OLS before execution. DGS/OTA is not required since this is a non-competitive method.</span>
                    </div>
                  </div>
                ) : (
                  // Within authority
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '4px 12px', borderRadius: 'var(--r-full)', background: 'var(--green-50)', border: '1.5px solid var(--green-100)', color: 'var(--green-600)', fontWeight: 700, fontSize: 12 }}>
                    ✓ Within Delegated Authority
                  </span>
                )}
                <div style={{ fontSize: 11, color: 'var(--slate-400)', marginTop: 4 }}>{dgsResult.reason}</div>
              </div>
            ) : null}
          </div>

          {/* Buttons */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6, flexShrink: 0 }}>
            <button
              className={`btn btn-sm ${contract.exemption?.active ? 'btn-danger' : 'btn-ghost'}`}
              style={{ fontSize: 12 }}
              onClick={() => updateC({ exemption: { ...(contract.exemption || {}), active: !contract.exemption?.active } })}
            >
              {contract.exemption?.active ? 'Remove Statutory Exemption' : '+ Add Statutory Exemption'}
            </button>
            {(dgsResult?.otaRequired || dgsResult?.otaExempted) && (
              <button
                className={`btn btn-sm ${contract.otaExemption?.active ? 'btn-danger' : 'btn-ghost'}`}
                style={{ fontSize: 12 }}
                onClick={() => updateC({ otaExemption: { ...(contract.otaExemption || {}), active: !contract.otaExemption?.active } })}
              >
                {contract.otaExemption?.active ? 'Remove OTA Exemption' : '+ Add OTA Exemption'}
              </button>
            )}
          </div>
        </div>

        {/* Exemption fields — shown when active */}
        {contract.exemption?.active && (
          <div style={{ marginTop: 16, paddingTop: 16, borderTop: '1px solid var(--slate-100)' }}>
            <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.07em', color: 'var(--slate-400)', marginBottom: 12 }}>
              Exemption Details — included in Penny's recommendations and the weekly report
            </div>
            <div className="form-grid-3">
              <Field
                label="Bill / Statute Reference"
                value={contract.exemption.statute || ''}
                onChange={v => updateC({ exemption: { ...contract.exemption, statute: v } })}
                placeholder="e.g., SB 123 (2025), PCC §1234"
                monospace
              />
              <Field
                label="Effective Date"
                value={contract.exemption.effectiveDate || ''}
                onChange={v => updateC({ exemption: { ...contract.exemption, effectiveDate: v } })}
                type="date"
              />
              <div className="form-field">
                <label>Exemptions Granted</label>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginTop: 4 }}>
                  {[
                    ['skipCompetitive', 'Competitive bidding waived'],
                    ['skipDGS',         'DGS/OLS approval waived'],
                    ['skipUnion',        'Union notification waived'],
                    ['skipProtest',      'Protest period waived'],
                  ].map(([key, label]) => (
                    <label key={key} style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 12.5 }}>
                      <input
                        type="checkbox"
                        checked={!!(contract.exemption[key])}
                        onChange={e => updateC({ exemption: { ...contract.exemption, [key]: e.target.checked } })}
                      />
                      {label}
                    </label>
                  ))}
                </div>
              </div>
              <Field
                label="Description / Authority"
                value={contract.exemption.description || ''}
                onChange={v => updateC({ exemption: { ...contract.exemption, description: v } })}
                rows={3}
                className="span-full"
                placeholder="Briefly describe the exemption authority — e.g., SB 123 grants energy safety agencies authority to select vendors without competitive bidding for time-sensitive infrastructure contracts…"
              />
            </div>
          </div>
        )}

        {/* OTA Exemption fields */}
        {dgsResult?.otaRequired && contract.otaExemption?.active && (
          <div style={{ marginTop: 16, paddingTop: 16, borderTop: '1px solid var(--slate-100)' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '3px 10px', borderRadius: 99, background: 'var(--teal-50)', border: '1.5px solid var(--teal-200)', color: 'var(--teal-700)', fontWeight: 700, fontSize: 11 }}>
                ✓ OTA Exemption Active
              </span>
              <span style={{ fontSize: 11, color: 'var(--slate-400)' }}>Agency may conduct this solicitation without DGS/OTA involvement</span>
            </div>
            <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.07em', color: 'var(--slate-400)', marginBottom: 12 }}>
              OTA Exemption Details — document in procurement file
            </div>
            <div className="form-grid">
              <div className="form-field">
                <label>Exemption Basis</label>
                <select
                  value={contract.otaExemption.basis || ''}
                  onChange={e => updateC({ otaExemption: { ...contract.otaExemption, basis: e.target.value } })}
                  style={{ width: '100%', padding: '8px 10px', borderRadius: 'var(--r-md)', border: '1.5px solid var(--slate-200)', fontSize: 13 }}
                >
                  <option value="">Select basis…</option>
                  <option value="STD.821 Approved">STD.821 Approved — Advertising Exemption Request</option>
                  <option value="Emergency Contract">Emergency Contract (PCC §10340)</option>
                  <option value="Categorical Exemption">Categorical Exemption by Policy</option>
                  <option value="Interagency Agreement">Interagency Agreement — Competitive bidding not required</option>
                  <option value="PAIR Approved">PAIR Approved — Purchasing Authority Increase Request</option>
                  <option value="Other">Other</option>
                </select>
              </div>
              <Field
                label="STD.821 Reference / Approval Info"
                value={contract.otaExemption.reference || ''}
                onChange={v => updateC({ otaExemption: { ...contract.otaExemption, reference: v } })}
                placeholder="Optional — approval date, reference number, or approving authority"
                monospace
              />
              <Field
                label="Justification Notes"
                value={contract.otaExemption.justification || ''}
                onChange={v => updateC({ otaExemption: { ...contract.otaExemption, justification: v } })}
                rows={3}
                className="span-full"
                placeholder="Document why this contract is exempt from OTA involvement — this language will appear in the procurement file record…"
              />
            </div>
          </div>
        )}
      </div>

      {/* ── Assigned Contract Manager ──────────────────────────── */}
      <div className="card" style={{ borderLeft: '3px solid var(--navy-600)' }}>
        <div className="card-title-row" onClick={() => setCollapsedCM(p => !p)} style={{ cursor: 'pointer', userSelect: 'none' }}>
          <h3 className="card-title" style={{ marginBottom: 0 }}>Assigned State Contract Manager</h3>
          <span style={{ color: 'var(--slate-400)', fontSize: 14, transition: 'transform .2s', transform: collapsedCM ? 'rotate(-90deg)' : 'rotate(0deg)', display: 'inline-block' }}>▼</span>
          {activeCms.length === 0 && (
            <a href="#cms"
              onClick={e => { e.preventDefault(); document.dispatchEvent(new CustomEvent('navigate', { detail: 'cms' })); }}
              style={{ fontSize: 12, color: 'var(--navy-500)', textDecoration: 'underline', cursor: 'pointer' }}>
              Add CMs to roster →
            </a>
          )}
        </div>
        {!collapsedCM && (<>

        {activeCms.length === 0 ? (
          <Callout type="blue">
            No contract managers in the roster yet. Go to <strong>CM Roster</strong> in the sidebar to add your team members.
          </Callout>
        ) : (
          <>
            <div style={{ marginBottom: assignedCm ? 16 : 0 }}>
              <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--slate-500)', textTransform: 'uppercase', letterSpacing: '.05em', display: 'block', marginBottom: 6 }}>
                Select Contract Manager
              </label>
              <select
                value={contract.assignedCmId || ''}
                onChange={e => assignCM(e.target.value)}
                style={{
                  width: '100%', padding: '9px 32px 9px 12px',
                  border: '1.5px solid ' + (assignedCm ? 'var(--navy-400)' : 'var(--slate-200)'),
                  borderRadius: 'var(--r-md)', fontSize: 13.5, fontFamily: 'var(--font-sans)',
                  background: 'var(--white)', cursor: 'pointer',
                  backgroundImage: "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='8' viewBox='0 0 12 8'%3E%3Cpath d='M1 1l5 5 5-5' stroke='%239CA3AF' stroke-width='1.5' fill='none' stroke-linecap='round'/%3E%3C%2Fsvg%3E\")",
                  backgroundRepeat: 'no-repeat', backgroundPosition: 'right 12px center',
                  appearance: 'none',
                  fontWeight: assignedCm ? 600 : 400,
                  color: assignedCm ? 'var(--navy-800)' : 'var(--slate-400)',
                }}
              >
                <option value="">— Not yet assigned —</option>
                {activeCms.map(cm => (
                  <option key={cm.id} value={cm.id}>{cm.name}{cm.title ? ' — ' + cm.title : ''}</option>
                ))}
              </select>
              <p style={{ fontSize: 11, color: 'var(--slate-400)', marginTop: 5 }}>
                The assigned CM is tracked for handoff and display purposes. STD 215 contact fields (6a–6c) remain your profile info.
              </p>
            </div>

            {/* Assigned CM card */}
            {assignedCm && (
              <div style={{
                display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
                gap: 10, padding: '14px 16px',
                background: 'linear-gradient(135deg, #EFF3FA 0%, #fff 100%)',
                border: '1.5px solid var(--navy-200)', borderRadius: 'var(--r-md)',
              }}>
                {[
                  ['Name',     assignedCm.name],
                  ['Title',    assignedCm.title],
                  ['Phone',    assignedCm.phone],
                  ['Email',    assignedCm.email],
                  ['Division', assignedCm.division],
                  ['Address',  assignedCm.address],
                ].filter(([, v]) => v).map(([label, val]) => (
                  <div key={label}>
                    <div style={{ fontSize: 9.5, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.06em', color: 'var(--slate-400)', marginBottom: 2 }}>{label}</div>
                    <div style={{ fontSize: 13, color: 'var(--navy-800)', fontFamily: label === 'Phone' || label === 'Email' ? 'var(--font-mono)' : 'inherit', fontSize: label === 'Phone' || label === 'Email' ? 12 : 13 }}>{val}</div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </>)}
      </div>

      {/* ── CM SOW Briefing ──────────────────────────────────── */}
      {/* CM SOW Briefing — inline implementation */}
      <div className="card" style={{ borderLeft: hasCmBriefing ? '3px solid var(--teal-400)' : '3px solid var(--slate-200)' }}>
        <div className="card-title-row" onClick={() => setCmBriefExpanded(p => !p)} style={{ cursor: 'pointer', userSelect: 'none' }}>
          <div>
            <h3 className="card-title" style={{ marginBottom: 0 }}>
              {hasCmBriefing ? '✦ CM SOW Briefing' : 'CM SOW Briefing'}
            </h3>
            {hasCmBriefing && contract.pennyDrafts?.sowRefNum && (
              <div style={{ fontSize: 11, color: 'var(--teal-600)', marginTop: 2, fontFamily: 'monospace' }}>{contract.pennyDrafts.sowRefNum}</div>
            )}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            {hasCmBriefing && (
              <span style={{ fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 99, background: 'var(--teal-100)', color: 'var(--teal-700)', border: '1px solid var(--teal-200)' }}>
                ✓ Loaded
              </span>
            )}
            <span style={{ color: 'var(--slate-400)', fontSize: 14, transition: 'transform .2s', transform: cmBriefExpanded ? 'rotate(0deg)' : 'rotate(-90deg)', display: 'inline-block' }}>▼</span>
          </div>
        </div>

        {cmBriefExpanded && (
          <>
            {!hasCmBriefing ? (
              <div style={{ marginTop: 12 }}>
                <p style={{ fontSize: 12, color: 'var(--slate-500)', marginBottom: 12, lineHeight: 1.5 }}>
                  No CM SOW session linked to this contract. Penny automatically links one when a CM SOW reference number is found in the request document at contract creation.
                </p>
                <p style={{ fontSize: 11.5, color: 'var(--slate-400)', marginBottom: 12 }}>
                  If you have a reference number, enter it manually:
                </p>
                <SOWBriefingPanel contract={contract} updateC={updateC} apiKey={apiKey} callAI={callAI} toast={toast} />
              </div>
            ) : (
              <div style={{ marginTop: 12 }}>
                {/* CM Profile */}
                {contract.pennyDrafts?.sowBriefing?.cm_profile && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 14px', background: 'var(--teal-50)', borderRadius: 'var(--r-md)', border: '1px solid var(--teal-200)', marginBottom: 12 }}>
                    <div style={{ width: 36, height: 36, borderRadius: '50%', background: 'var(--teal-500)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontWeight: 700, fontSize: 16, flexShrink: 0 }}>
                      {(contract.pennyDrafts.sowBriefing.cm_profile.name || 'CM')[0]}
                    </div>
                    <div>
                      <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--teal-800)' }}>{contract.pennyDrafts.sowBriefing.cm_profile.name || 'Contract Manager'}</div>
                      {contract.pennyDrafts.sowBriefing.cm_profile.title && <div style={{ fontSize: 11.5, color: 'var(--teal-600)' }}>{contract.pennyDrafts.sowBriefing.cm_profile.title}</div>}
                      {contract.pennyDrafts.sowBriefing.cm_profile.email && <div style={{ fontSize: 11, color: 'var(--teal-500)' }}>{contract.pennyDrafts.sowBriefing.cm_profile.email}</div>}
                    </div>
                    {contract.pennyDrafts.sowBriefing.estimated_budget && (
                      <div style={{ marginLeft: 'auto', textAlign: 'right' }}>
                        <div style={{ fontSize: 10, color: 'var(--teal-500)', textTransform: 'uppercase', letterSpacing: '.05em' }}>CM Estimated Budget</div>
                        <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--teal-800)' }}>{contract.pennyDrafts.sowBriefing.estimated_budget}</div>
                      </div>
                    )}
                  </div>
                )}

                {/* Penny Briefing Text */}
                {contract.pennyDrafts?.pennyBriefText ? (
                  <div style={{ padding: '14px 16px', background: 'var(--navy-50)', borderRadius: 'var(--r-md)', border: '1px solid var(--navy-100)' }}>
                    <div style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.06em', color: 'var(--gold-600)', marginBottom: 8 }}>✦ Penny Briefing</div>
                    <div style={{ fontSize: 12.5, lineHeight: 1.8, color: 'var(--navy-800)', whiteSpace: 'pre-wrap' }}>{contract.pennyDrafts.pennyBriefText}</div>
                  </div>
                ) : (
                  <div style={{ padding: '12px 14px', background: 'var(--slate-50)', borderRadius: 'var(--r-md)', fontSize: 12, color: 'var(--slate-500)', textAlign: 'center' }}>
                    Penny briefing not yet generated.
                    <button className="btn btn-gold btn-sm" style={{ marginLeft: 10 }}
                      onClick={async () => {
                        try {
                          const b = contract.pennyDrafts?.sowBriefing;
                          if (!b) return;
                          const messages = (b.messages || []).filter(m => m.role === 'user').map(m => m.content).join('\n');
                          const system = 'You are Penny briefing a contract analyst. Summarize the CM SOW session concisely: 1. CM INTENT 2. KEY ANSWERS (Five Ws) 3. BUDGET 4. AREAS OF UNCERTAINTY 5. COMPLIANCE FLAGS 6. ANALYST RECOMMENDATIONS. Under 400 words. Be specific.';
                          const userMsg = 'SOW Reference: ' + (b.reference_number || '') + '\nCM: ' + (b.cm_profile?.name || '') + '\nIdea: ' + (b.initial_idea || '') + '\nFive Ws: ' + JSON.stringify(b.five_ws || {}) + '\nBudget: ' + (b.estimated_budget || '') + '\nConversation: ' + messages.slice(0, 2000);
                          const text = await callAI(apiKey, { system, messages: [{ role: 'user', content: userMsg }], max_tokens: 600 });
                          updateC({ pennyDrafts: { pennyBriefText: text } });
                          toast.success('Penny briefing generated');
                        } catch (e) { toast.error('Briefing failed: ' + e.message); }
                      }}>
                      ✦ Generate Briefing
                    </button>
                  </div>
                )}

                {/* Clear */}
                <div style={{ marginTop: 10, textAlign: 'right' }}>
                  <button className="btn btn-ghost btn-xs" style={{ color: 'var(--slate-400)' }}
                    onClick={() => { updateC({ pennyDrafts: { sowBriefing: null, sowRefNum: '', pennyBriefText: '' } }); toast.info('CM briefing cleared'); }}>
                    ✕ Clear briefing
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* ── Awarded Contractor ───────────────────────────────────── */}
      <div className="card">
        <div className="card-title-row" onClick={() => setCollapsedContractor(p => !p)} style={{ cursor: 'pointer', userSelect: 'none' }}>
          <div>
            <h3 className="card-title" style={{ marginBottom: 0 }}>Awarded Contractor</h3>
            {s215.contractorName && !collapsedContractor === false && (
              <div style={{ fontSize: 11, color: 'var(--slate-500)', marginTop: 2 }}>{s215.contractorName}</div>
            )}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            {s215.contractorName && (
              <span style={{ fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 99, background: 'var(--green-100)', color: 'var(--green-700)', border: '1px solid var(--green-200)' }}>
                ✓ {s215.contractorName}
              </span>
            )}
            <span style={{ color: 'var(--slate-400)', fontSize: 14, transition: 'transform .2s', transform: collapsedContractor ? 'rotate(-90deg)' : 'rotate(0deg)', display: 'inline-block' }}>▼</span>
          </div>
        </div>

        {!collapsedContractor && (() => {
          const vendorRecord = vendors.find(v => v.id === contract.contractorVendorId || v.company_name === s215.contractorName);
          const vendorDocs = vendorRecord?.docs || {};
          const vendorDocMeta = vendorRecord?.doc_metadata || {};
          const DOC_LABELS = { std204: 'STD 204', w9: 'W-9', sb_cert: 'SB Cert', dvbe_cert: 'DVBE Cert', license: 'Biz License', gl: 'GL Ins.', wc: "Workers' Comp", auto: 'Auto Ins.', eo: 'E&O', bond: 'Bond', other: 'Other' };
          const onFileDocs = Object.entries(vendorDocs).filter(([, v]) => v?.name);
          const daysTilFn = d => d ? Math.ceil((new Date(d) - new Date()) / 86400000) : null;
          return (
          <div style={{ marginTop: 12 }}>
            {!s215.contractorName ? (
              <div style={{ padding: '16px', background: 'var(--slate-50)', borderRadius: 'var(--r-md)', fontSize: 12.5, color: 'var(--slate-400)', textAlign: 'center' }}>
                No contractor selected yet — enter contractor information in the <strong>Funding & Contract Information</strong> section below.
              </div>
            ) : (
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                {/* Company info */}
                <div style={{ gridColumn: 'span 2', padding: '14px 16px', background: 'var(--slate-50)', borderRadius: 'var(--r-md)', border: '1px solid var(--slate-200)' }}>
                  <div style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.06em', color: 'var(--slate-400)', marginBottom: 10 }}>Company</div>
                  <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--navy-800)', marginBottom: 4 }}>{s215.contractorName}</div>
                  {s215.contractorTaxId && <div style={{ fontSize: 12, color: 'var(--slate-500)' }}>Tax ID: <span style={{ fontFamily: 'monospace' }}>{s215.contractorTaxId}</span></div>}
                  {s215.contractorAddress && (
                    <div style={{ fontSize: 12, color: 'var(--slate-500)', marginTop: 4 }}>
                      {s215.contractorAddress}{s215.contractorCity ? ', ' + s215.contractorCity : ''}{s215.contractorState ? ', ' + s215.contractorState : ''}{s215.contractorZip ? ' ' + s215.contractorZip : ''}
                    </div>
                  )}
                  {(s215.contractorPhone || s215.contractorEmail) && (
                    <div style={{ display: 'flex', gap: 16, marginTop: 6, fontSize: 12, color: 'var(--slate-500)' }}>
                      {s215.contractorPhone && <span>📞 {s215.contractorPhone}</span>}
                      {s215.contractorEmail && <span>✉ {s215.contractorEmail}</span>}
                    </div>
                  )}
                </div>

                {/* Create vendor profile prompt */}
                {!vendorRecord && s215.contractorName && (
                  <div style={{ gridColumn: 'span 2', padding: '10px 14px', background: 'var(--gold-50)', borderRadius: 'var(--r-md)', border: '1px solid var(--gold-200)', display: 'flex', alignItems: 'center', gap: 12 }}>
                    <span style={{ fontSize: 18 }}>✦</span>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--navy-700)' }}>{s215.contractorName} is not in your Vendor Library</div>
                      <div style={{ fontSize: 11.5, color: 'var(--slate-500)', marginTop: 2 }}>Penny can create a profile now using the information already on file — you can add documents and certifications later.</div>
                    </div>
                    <button className="btn btn-gold btn-sm" onClick={createVendorFromContract}>
                      + Create Vendor Profile
                    </button>
                  </div>
                )}

                {/* Contractor CM */}
                {s215.contractorContactName && (
                  <div style={{ gridColumn: 'span 2', padding: '14px 16px', background: 'var(--slate-50)', borderRadius: 'var(--r-md)', border: '1px solid var(--slate-200)' }}>
                    <div style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.06em', color: 'var(--slate-400)', marginBottom: 10 }}>Contractor Contract Manager</div>
                    <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--navy-700)' }}>{s215.contractorContactName}</div>
                    {s215.contractorContactTitle && <div style={{ fontSize: 12, color: 'var(--slate-500)', marginTop: 2 }}>{s215.contractorContactTitle}</div>}
                    <div style={{ display: 'flex', gap: 16, marginTop: 6, fontSize: 12, color: 'var(--slate-500)' }}>
                      {s215.contractorContactPhone && <span>📞 {s215.contractorContactPhone}</span>}
                      {s215.contractorContactEmail && <span>✉ {s215.contractorContactEmail}</span>}
                    </div>
                  </div>
                )}

                {/* Document tags */}
                {onFileDocs.length > 0 && (
                  <div style={{ gridColumn: 'span 2', marginTop: 4 }}>
                    <div style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.06em', color: 'var(--slate-400)', marginBottom: 6 }}>Documents on File</div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                      {onFileDocs.map(([key, doc]) => {
                        const meta = vendorDocMeta[key];
                        const expDays = meta?.expiration_date ? daysTilFn(meta.expiration_date) : null;
                        const isExpired = expDays !== null && expDays < 0;
                        const isExpiring = expDays !== null && expDays >= 0 && expDays <= 30;
                        const color = isExpired ? 'var(--red-600)' : isExpiring ? 'var(--orange-600)' : 'var(--teal-700)';
                        const bg = isExpired ? 'var(--red-50)' : isExpiring ? 'var(--orange-50)' : 'var(--teal-50)';
                        const border = isExpired ? 'var(--red-200)' : isExpiring ? 'var(--orange-200)' : 'var(--teal-200)';
                        return (
                          <span key={key} style={{ fontSize: 11, fontWeight: 600, padding: '3px 9px', borderRadius: 99, background: bg, color, border: '1px solid ' + border, display: 'flex', alignItems: 'center', gap: 4 }}>
                            {isExpired ? '⚠' : isExpiring ? '⚠' : '✓'} {DOC_LABELS[key] || key}
                            {meta?.expiration_date && <span style={{ fontWeight: 400, fontSize: 10 }}>· {fmtDate(meta.expiration_date)}</span>}
                          </span>
                        );
                      })}
                    </div>
                    {onFileDocs.length === 0 && vendorRecord && (
                      <span style={{ fontSize: 12, color: 'var(--slate-400)' }}>No documents on file in vendor profile</span>
                    )}
                  </div>
                )}

                {/* SB/DVBE cert */}
                {contract.sbDvbeCertNum && (
                  <div style={{ gridColumn: 'span 2', padding: '10px 14px', background: 'var(--teal-50)', borderRadius: 'var(--r-md)', border: '1px solid var(--teal-200)', fontSize: 12, color: 'var(--teal-700)' }}>
                    SB/DVBE Cert #: <strong style={{ fontFamily: 'monospace' }}>{contract.sbDvbeCertNum}</strong>
                  </div>
                )}
              </div>
            )}
          </div>
          );
        })()}
      </div>
      {/* ── Funding & Contract Information ──────────────────────── */}
      <div className="card">
        <div className="card-title-row" onClick={() => setCollapsedSTD215(p => !p)} style={{ cursor: 'pointer', userSelect: 'none' }}>
          <h3 className="card-title" style={{ marginBottom: 0 }}>Funding & Contract Information</h3>
          <span style={{ color: 'var(--slate-400)', fontSize: 14, transition: 'transform .2s', transform: collapsedSTD215 ? 'rotate(-90deg)' : 'rotate(0deg)', display: 'inline-block' }}>▼</span>
        </div>
        {!collapsedSTD215 && (<>

        <SectionLabel>Contract Identification</SectionLabel>
        <div className="form-grid-3">
          <Field label="Amendment Number" value={s215.amendmentNum} onChange={v => updateS215('amendmentNum', v)} monospace placeholder="N/A if original" />
          <Field label="Agency Billing Code" value={s215.agencyBillingCode} onChange={v => updateS215('agencyBillingCode', v)} monospace autoTag={isAuto('agencyBillingCode', profile.agencyBillingCode)} />
          {(() => {
            const agencyCode = profile.scoAgencyCode || '3355';
            const autoScoId = contract.contractNum ? agencyCode + '-' + contract.contractNum : '';
            const displayScoId = s215.scoId || autoScoId;
            return (
              <div style={{ gridColumn: 'span 1' }}>
                <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.05em', color: 'var(--slate-500)', marginBottom: 4 }}>SCO ID</div>
                {autoScoId && !s215.scoId ? (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 10px', background: 'var(--teal-50)', border: '1.5px solid var(--teal-200)', borderRadius: 'var(--r-md)' }}>
                    <span style={{ fontFamily: 'var(--font-mono)', fontSize: 13.5, fontWeight: 600, color: 'var(--teal-700)', flex: 1 }}>{autoScoId}</span>
                    <span style={{ fontSize: 10, color: 'var(--teal-500)' }}>Auto</span>
                    <button onClick={() => updateS215('scoId', autoScoId)} style={{ fontSize: 10, color: 'var(--slate-400)', background: 'none', border: 'none', cursor: 'pointer', textDecoration: 'underline' }}>Override</button>
                  </div>
                ) : (
                  <input
                    value={s215.scoId || ''}
                    onChange={e => updateS215('scoId', e.target.value)}
                    placeholder={autoScoId || (profile.scoAgencyCode || '3355') + '-[Contract No.]'}
                    style={{ width: '100%', padding: '8px 10px', border: '1.5px solid var(--slate-200)', borderRadius: 'var(--r-md)', fontSize: 13, fontFamily: 'var(--font-mono)', boxSizing: 'border-box' }}
                  />
                )}
                <div style={{ fontSize: 10.5, color: 'var(--slate-400)', marginTop: 3 }}>
                  {autoScoId && s215.scoId ? <button onClick={() => updateS215('scoId', '')} style={{ fontSize: 10.5, color: 'var(--slate-400)', background: 'none', border: 'none', cursor: 'pointer', textDecoration: 'underline', padding: 0 }}>Reset to auto ({autoScoId})</button> : 'Format: {agencyCode}-{contractNum}. Auto-generated from your contract number.'}
                </div>
              </div>
            );
          })()}
          <Field label="Start Date" value={s215.startDate} onChange={v => updateS215('startDate', v)} type="date" />
          <Field label="End Date" value={s215.endDate} onChange={v => updateS215('endDate', v)} type="date" />
          <Field label="Maximum Amount ($)" value={String(s215.maxAmount || contract.budget || "").replace(/[$,]/g, "")} onChange={v => updateS215('maxAmount', v)} type="number" />
        </div>

        {/* ── Funding Lines ──────────────────────────────────────── */}
        <SectionLabel>Funding Lines (Section 5 — STD 215)</SectionLabel>
        <p style={{ fontSize: 12.5, color: 'var(--slate-500)', marginBottom: 12 }}>
          Add one row per funding source. All amounts should sum to the contract maximum. These populate directly onto the STD 215 export.
        </p>

        {/* Funding lines table */}
        {(s215.fundingLines || []).length > 0 && (
          <div style={{ marginBottom: 12, overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12.5 }}>
              <thead>
                <tr style={{ background: 'var(--navy-50)' }}>
                  {['Fund Title', 'Item', 'Fiscal Year', 'Chapter', 'Statute', 'Projected Expenditures', ''].map(h => (
                    <th key={h} style={{ padding: '6px 8px', textAlign: 'left', fontSize: 10.5, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.04em', color: 'var(--slate-500)', borderBottom: '1.5px solid var(--slate-200)', whiteSpace: 'nowrap' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {(s215.fundingLines || []).map((line, idx) => (
                  <tr key={line.id} style={{ borderBottom: '1px solid var(--slate-100)' }}>
                    {[
                      { key: 'fundTitle',  placeholder: 'e.g. General Fund' },
                      { key: 'item',       placeholder: 'e.g. 001' },
                      { key: 'fiscalYear', placeholder: 'e.g. 2025-26' },
                      { key: 'chapter',    placeholder: 'e.g. 23' },
                      { key: 'statute',    placeholder: 'e.g. Item 3360' },
                    ].map(({ key, placeholder }) => (
                      <td key={key} style={{ padding: '4px 6px' }}>
                        <input
                          value={line[key] || ''}
                          onChange={e => {
                            const updated = (s215.fundingLines || []).map((l, i) => i === idx ? { ...l, [key]: e.target.value } : l);
                            updateS215('fundingLines', updated);
                          }}
                          style={{ width: '100%', padding: '5px 8px', border: '1px solid var(--slate-200)', borderRadius: 4, fontSize: 12.5, fontFamily: 'var(--font-mono)' }}
                          placeholder={placeholder}
                        />
                      </td>
                    ))}
                    <td style={{ padding: '4px 6px' }}>
                      <input
                        type="number"
                        value={line.amount || ''}
                        onChange={e => {
                          const updated = (s215.fundingLines || []).map((l, i) => i === idx ? { ...l, amount: e.target.value } : l);
                          updateS215('fundingLines', updated);
                        }}
                        style={{ width: 110, padding: '5px 8px', border: '1px solid var(--slate-200)', borderRadius: 4, fontSize: 12.5 }}
                        placeholder="$0.00"
                      />
                    </td>
                    <td style={{ padding: '4px 6px' }}>
                      <button
                        onClick={() => updateS215('fundingLines', (s215.fundingLines || []).filter((_, i) => i !== idx))}
                        style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--red-400)', fontSize: 16, padding: '0 4px' }}
                        title="Remove row"
                      >×</button>
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr style={{ background: 'var(--slate-50)', borderTop: '2px solid var(--slate-200)' }}>
                  <td colSpan={5} style={{ padding: '6px 8px', fontSize: 12, fontWeight: 700, color: 'var(--slate-600)' }}>Total</td>
                  <td style={{ padding: '6px 8px', fontSize: 12.5, fontWeight: 700, color: (() => {
                    const total = (s215.fundingLines || []).reduce((sum, l) => sum + (parseFloat(l.amount) || 0), 0);
                    const max = parseFloat(s215.maxAmount || contract.budget) || 0;
                    return Math.abs(total - max) > 0.01 && max > 0 ? 'var(--red-600)' : 'var(--green-700)';
                  })() }}>
                    {new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(
                      (s215.fundingLines || []).reduce((sum, l) => sum + (parseFloat(l.amount) || 0), 0)
                    )}
                  </td>
                  <td />
                </tr>
              </tfoot>
            </table>

            {/* Validation warning */}
            {(() => {
              const total = (s215.fundingLines || []).reduce((sum, l) => sum + (parseFloat(l.amount) || 0), 0);
              const max = parseFloat(s215.maxAmount || contract.budget) || 0;
              if (max > 0 && Math.abs(total - max) > 0.01) {
                return (
                  <div style={{ marginTop: 8, padding: '8px 12px', background: 'var(--red-50)', border: '1px solid var(--red-200)', borderRadius: 'var(--r-md)', fontSize: 12, color: 'var(--red-700)' }}>
                    ⚠ Funding lines total ({new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(total)}) does not match contract maximum ({new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(max)}). DGS will flag this discrepancy.
                  </div>
                );
              }
              return null;
            })()}
          </div>
        )}

        <button
          className="btn btn-ghost btn-sm"
          onClick={() => {
            const newLine = { id: Date.now().toString(), encumbranceNum: '', fund: '', orgCode: '', objectCode: '', fiscalYear: '', amount: '' };
            updateS215('fundingLines', [...(s215.fundingLines || []), newLine]);
          }}
        >
          + Add Funding Line
        </button>

        <SectionLabel>Contractor Information</SectionLabel>
        {vendors.length > 0 && (
          <div style={{ marginBottom: 12, display: 'flex', gap: 10, alignItems: 'center' }}>
            <select
              value={contract.contractorVendorId || ''}
              onChange={e => { if (e.target.value) applyVendor(e.target.value); }}
              style={{ flex: 1, padding: '8px 10px', borderRadius: 'var(--r-md)', border: '1.5px solid var(--gold-400)', fontSize: 13, background: 'var(--navy-900)', color: 'var(--gold-400)', fontWeight: 600 }}
            >
              <option value="">⚡ Select from Vendor Library to auto-fill…</option>
              {vendors.map(v => (
                <option key={v.id} value={v.id}>
                  {v.company_name}{v.sb_certified ? ' — SB' : ''}{v.dvbe_certified ? ' DVBE' : ''}
                </option>
              ))}
            </select>
            {contract.contractorVendorId && (
              <button className="btn btn-ghost btn-xs" style={{ color: 'var(--slate-400)', whiteSpace: 'nowrap' }}
                onClick={() => updateC({ contractorVendorId: null })}>✕ Unlink</button>
            )}
          </div>
        )}
        <div className="form-grid">
          <Field label="Contractor Legal Name" value={s215.contractorName} onChange={v => updateS215('contractorName', v)} className="span-full" />
          <Field label="Federal Tax ID" value={s215.contractorTaxId} onChange={v => updateS215('contractorTaxId', v)} monospace placeholder="XX-XXXXXXX" />
          <Select label="Procurement Method (STD 215)" value={s215.procMethod} onChange={v => {
            if (!v || v === s215.procMethod) return;
            const cacheKey = (contract.procMethod || 'unspecified') + '_' + (contract.isIT || 'no');
            const hasCachedBrief = contract.pennyDrafts?.scmContext?.[cacheKey];
            if (hasCachedBrief && v !== contract.procMethod) {
              setPendingProcMethod(v);
            } else {
              updateS215('procMethod', v);
            }
          }}>
            <option value="">Select…</option>
            {['Competitive Bid', 'NCB', 'Sole Source', 'Exempt', 'RFQ', 'RFO', 'IFB'].map(m => <option key={m} value={m}>{m}</option>)}
          </Select>
        </div>
        <div className="form-grid-3" style={{ marginTop: 12 }}>
          <Field label="Contractor Address" value={s215.contractorAddress} onChange={v => updateS215('contractorAddress', v)} />
          <Field label="City" value={s215.contractorCity} onChange={v => updateS215('contractorCity', v)} />
          <Field label="State" value={s215.contractorState} onChange={v => updateS215('contractorState', v)} />
          <Field label="ZIP Code" value={s215.contractorZip} onChange={v => updateS215('contractorZip', v)} monospace />
          <Field label="Contractor Phone" value={s215.contractorPhone} onChange={v => updateS215('contractorPhone', v)} />
          <Field label="Contractor Email" value={s215.contractorEmail} onChange={v => updateS215('contractorEmail', v)} />
          <Field label="SB/DVBE Certification Number" value={contract.sbDvbeCertNum || ''} onChange={v => updateC({ sbDvbeCertNum: v })} monospace placeholder="e.g., 1234567" hint="Populates STD 215 Page 2, Field 23" />
        </div>

        {expandedS215 && (
          <>
            <SectionLabel>Contractor CM (for Exhibit A)</SectionLabel>
            <div className="form-grid">
              <Field label="Contractor CM Name" value={s215.contractorContactName} onChange={v => updateS215('contractorContactName', v)} />
              <Field label="Contractor CM Title" value={s215.contractorContactTitle} onChange={v => updateS215('contractorContactTitle', v)} />
              <Field label="Contractor CM Phone" value={s215.contractorContactPhone} onChange={v => updateS215('contractorContactPhone', v)} />
              <Field label="Contractor CM Email" value={s215.contractorContactEmail} onChange={v => updateS215('contractorContactEmail', v)} />
            </div>
          </>
        )}


        {expandedS215 && (
          <>
            <SectionLabel>Contracting Out Justification</SectionLabel>
            <div className="form-grid">
              <Field label="Agreement Outline / Scope Summary" value={s215.agreeOutline} onChange={v => updateS215('agreeOutline', v)} rows={4} className="span-full"
                placeholder="Describe the nature of the agreement and services to be provided" />
              <Field label="Justification (GC 19130 / PCC 10295)" value={s215.justification547} onChange={v => updateS215('justification547', v)} rows={4} className="span-full"
                placeholder="State the basis for contracting out this work" />
            </div>

            <SectionLabel>Routing & Approval Status</SectionLabel>
            <div className="form-grid-3">
              <div className="form-field">
                <label>DGS/OLS Routed</label>
                <select value={s215.dgsRouted ? 'yes' : 'no'} onChange={e => updateS215('dgsRouted', e.target.value === 'yes')}>
                  <option value="no">No</option><option value="yes">Yes</option>
                </select>
              </div>
              {s215.dgsRouted && <Field label="DGS Routed Date" value={s215.dgsRoutedDate} onChange={v => updateS215('dgsRoutedDate', v)} type="date" />}
              <div className="form-field">
                <label>DGS/OLS Approved</label>
                <select value={s215.dgsApproved ? 'yes' : 'no'} onChange={e => updateS215('dgsApproved', e.target.value === 'yes')}>
                  <option value="no">No</option><option value="yes">Yes</option>
                </select>
              </div>
              {s215.dgsApproved && <Field label="DGS Approved Date" value={s215.dgsApprovedDate} onChange={v => updateS215('dgsApprovedDate', v)} type="date" />}
              <div className="form-field">
                <label>Union Notified (GC 19131)</label>
                <select value={s215.unionNotified ? 'yes' : 'no'} onChange={e => updateS215('unionNotified', e.target.value === 'yes')}>
                  <option value="no">No</option><option value="yes">Yes</option>
                </select>
              </div>
              {s215.unionNotified && <Field label="Union Notified Date" value={s215.unionNotifiedDate} onChange={v => updateS215('unionNotifiedDate', v)} type="date" />}
            </div>

            <SectionLabel>STD 215 Compliance Checkboxes (Fields 10, 13, 17–25)</SectionLabel>
            <div className="form-grid">

              {/* Field 10 — Payment Terms */}
              <div className="form-field span-full">
                <label>Field 10 — Payment Terms <span style={{ fontWeight: 400, color: 'var(--slate-400)', fontSize: 11 }}>(check all that apply)</span></label>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, marginTop: 6 }}>
                  {['Itemized Invoice','Not To Exceed','Monthly Flat Rate','Quarterly','One-Time Payment','Progress Payment','Withhold','Advanced Payment','Reimbursement','Other'].map(term => {
                    const checked = (s215.paymentTerms || ['Itemized Invoice','Not To Exceed']).includes(term);
                    return (
                      <label key={term} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12.5, cursor: 'pointer',
                        padding: '5px 10px', borderRadius: 'var(--r-md)', border: '1.5px solid ' + (checked ? 'var(--navy-400)' : 'var(--slate-200)'),
                        background: checked ? 'var(--blue-50)' : 'var(--white)' }}>
                        <input type="checkbox" checked={checked} onChange={() => {
                          const current = s215.paymentTerms || ['Itemized Invoice','Not To Exceed'];
                          updateS215('paymentTerms', checked ? current.filter(t => t !== term) : [...current, term]);
                        }} style={{ margin: 0 }} />
                        {term}
                      </label>
                    );
                  })}
                </div>
              </div>

              {/* Field 13 — Bidding Method (driven by procMethod, shown read-only) */}
              <div className="form-field">
                <label>Field 13 — Bidding Method</label>
                <div style={{ fontSize: 12.5, color: 'var(--slate-600)', padding: '8px 10px', background: 'var(--slate-50)', borderRadius: 'var(--r-md)', border: '1px solid var(--slate-200)' }}>
                  Auto-set from Procurement Method: <strong>{contract.procMethod || 'Not set'}</strong>
                </div>
              </div>

              {/* Field 17a — GC 19130 */}
              <div className="form-field">
                <label>Field 17a — Contracting Out Justification</label>
                <select value={s215.gc19130 || 'b'} onChange={e => updateS215('gc19130', e.target.value)}>
                  <option value="a">GC 19130(a) — Cost savings (State Personnel Board notified)</option>
                  <option value="b">GC 19130(b) — Other justification (attach Section 547.60)</option>
                  <option value="na">Not Applicable (Interagency / Public Works / Other)</option>
                </select>
              </div>

              {/* Field 17b — Bargaining Unit */}
              <div className="form-field">
                <label>Field 17b — Employee Bargaining Unit Notification</label>
                <select value={s215.bargainingUnitNotified !== false ? 'yes' : 'no'} onChange={e => updateS215('bargainingUnitNotified', e.target.value === 'yes')}>
                  <option value="yes">✓ Certified — GC 19132(b)(1) compliance confirmed</option>
                  <option value="no">Not yet certified</option>
                </select>
              </div>

              {/* Field 18 — Fair Employment */}
              <div className="form-field">
                <label>Field 18 — Fair Employment Reporting <span style={{ fontSize: 11, color: 'var(--slate-400)' }}>(agreements over ,000)</span></label>
                <select value={s215.fairEmployment || 'N/A'} onChange={e => updateS215('fairEmployment', e.target.value)}>
                  <option value="No">No</option>
                  <option value="Yes">Yes</option>
                  <option value="N/A">N/A</option>
                </select>
              </div>

              {/* Field 19 — Conflict of Interest */}
              <div className="form-field">
                <label>Field 19 — Conflict of Interest Resolved <span style={{ fontSize: 11, color: 'var(--slate-400)' }}>(SCM Section 7.10)</span></label>
                <select value={s215.conflictOfInterest || 'Yes'} onChange={e => updateS215('conflictOfInterest', e.target.value)}>
                  <option value="No">No</option>
                  <option value="Yes">Yes</option>
                  <option value="N/A">N/A</option>
                </select>
              </div>

              {/* Field 20 — Consulting Evaluations */}
              <div className="form-field">
                <label>Field 20 — Contractor Evaluations Reviewed <span style={{ fontSize: 11, color: 'var(--slate-400)' }}>(consulting agreements only)</span></label>
                <select value={s215.consultingEvals || 'N/A'} onChange={e => updateS215('consultingEvals', e.target.value)}>
                  <option value="None on file">None on file</option>
                  <option value="No">No</option>
                  <option value="Yes">Yes</option>
                  <option value="N/A">N/A</option>
                </select>
              </div>

              {/* Field 21 — Contractor Certification Clauses */}
              <div className="form-field">
                <label>Field 21A — Contractor Certification Clauses on file</label>
                <select value={s215.certClauses || 'Yes'} onChange={e => updateS215('certClauses', e.target.value)}>
                  <option value="No">No</option>
                  <option value="Yes">Yes</option>
                  <option value="N/A">N/A</option>
                </select>
              </div>

              {/* Field 21B — STD 204 */}
              <div className="form-field">
                <label>Field 21B — STD 204 Vendor Data Record on file</label>
                <select value={s215.std204 || 'Yes'} onChange={e => updateS215('std204', e.target.value)}>
                  <option value="No">No</option>
                  <option value="Yes">Yes</option>
                  <option value="N/A">N/A</option>
                </select>
              </div>

              {/* Field 22 — Required Resolutions */}
              <div className="form-field">
                <label>Field 22 — Required Resolutions Attached</label>
                <select value={s215.resolutions || 'Yes'} onChange={e => updateS215('resolutions', e.target.value)}>
                  <option value="No">No</option>
                  <option value="Yes">Yes</option>
                  <option value="N/A">N/A</option>
                </select>
              </div>

              {/* Field 24 — DVBE Goals */}
              <div className="form-field">
                <label>Field 24 — DVBE Goals Required</label>
                <select value={s215.dvbeGoalsRequired || 'no'} onChange={e => updateS215('dvbeGoalsRequired', e.target.value)}>
                  <option value="no">No (explain below)</option>
                  <option value="yes">Yes</option>
                </select>
              </div>
              {s215.dvbeGoalsRequired === 'yes' && (
                <Field label="DVBE Goal %" value={s215.dvbePct || ''} onChange={v => updateS215('dvbePct', v)} placeholder="e.g., 3" hint="% of Agreement" />
              )}

              {/* Fields 14-16 — Bids / Price Basis */}
              <Field label="Field 14 — Summary of Bids" value={s215.summaryBids || ''} onChange={v => updateS215('summaryBids', v)} rows={3} className="span-full"
                placeholder="List bidders, bid amounts, and small business status. Leave blank if amendment, sole source, or exempt." />
              <Field label="Field 15 — Award to Other Than Low Bidder (if applicable)" value={s215.lowerBidderReason || ''} onChange={v => updateS215('lowerBidderReason', v)} rows={2} className="span-full"
                placeholder="If award is to other than the lowest bidder, explain reason(s). Leave blank if not applicable." />
              <Field label="Field 16 — Basis for Price Reasonableness" value={s215.priceBasis || ''} onChange={v => updateS215('priceBasis', v)} rows={2} className="span-full"
                placeholder="e.g., Competitive bidding conducted via CalEProcure. Lowest responsive bid selected." />
            </div>
          </>
        )}
      </>)}
      </div>

      {/* ── SB/DVBE Compliance Panel — Stage 7 ──────────────────── */}
      {contract.stage === 7 && (
        <div className="card" style={{ borderLeft: '3px solid var(--teal-500)' }}>
          <h3 className="card-title">✦ SB/DVBE Compliance Documentation</h3>
          <p style={{ fontSize: 12.5, color: 'var(--slate-500)', marginBottom: 16 }}>
            California state law requires documentation of Small Business (SB) and Disabled Veteran Business Enterprise (DVBE)
            participation efforts before award. Complete all applicable fields for your procurement file.
          </p>
          <div className="form-grid">
            {/* Solicitation Method */}
            <div className="form-field">
              <label>Solicitation Method Used</label>
              <select
                value={contract.dvbe?.solMethod || ''}
                onChange={e => updateC({ dvbe: { ...(contract.dvbe || {}), solMethod: e.target.value } })}
                style={{ width: '100%', padding: '8px 10px', borderRadius: 'var(--r-md)', border: '1.5px solid var(--slate-200)', fontSize: 13 }}
              >
                <option value="">Select…</option>
                <option value="Open Competition">Open Competition</option>
                <option value="SB Option">SB Option (GC §14838.5)</option>
                <option value="DVBE Option">DVBE Option (MVC §999.2)</option>
                <option value="NCB">Non-Competitive Bid (NCB)</option>
                <option value="Other">Other</option>
              </select>
            </div>

            {/* SB Participation — Goal and Incentive are independent */}
            <div className="form-field" style={{ gridColumn: '1 / -1' }}>
              <label>SB Participation</label>
              <div style={{ fontSize: 11, color: 'var(--slate-400)', marginBottom: 6 }}>Goal and Preference (incentive) are set independently.</div>
              <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                <div>
                  <div style={{ fontSize: 11, color: 'var(--slate-500)', marginBottom: 3 }}>Goal Requirement</div>
                  <select
                    value={contract.dvbe?.sbGoalStatus || ''}
                    onChange={e => updateC({ dvbe: { ...(contract.dvbe || {}), sbGoalStatus: e.target.value } })}
                    style={{ width: 190, padding: '8px 10px', borderRadius: 'var(--r-md)', border: '1.5px solid var(--slate-200)', fontSize: 13 }}
                  >
                    <option value="">Select…</option>
                    <option value="applies">Applies</option>
                    <option value="waived">Waived</option>
                    <option value="not_required">Not required</option>
                  </select>
                </div>
                <div>
                  <div style={{ fontSize: 11, color: 'var(--slate-500)', marginBottom: 3 }}>Preference (Incentive)</div>
                  <select
                    value={contract.dvbe?.sbIncentiveStatus || ''}
                    onChange={e => updateC({ dvbe: { ...(contract.dvbe || {}), sbIncentiveStatus: e.target.value } })}
                    style={{ width: 190, padding: '8px 10px', borderRadius: 'var(--r-md)', border: '1.5px solid var(--slate-200)', fontSize: 13 }}
                  >
                    <option value="">Select…</option>
                    <option value="applies">Applies</option>
                    <option value="waived">Waived</option>
                    <option value="not_required">Not required</option>
                  </select>
                </div>
              </div>
              {contract.dvbe?.sbGoalStatus === 'applies' && (
                <div style={{ marginTop: 6, display: 'flex', alignItems: 'center', gap: 6 }}>
                  <span style={{ fontSize: 11.5, color: 'var(--slate-500)' }}>Goal %:</span>
                  <input
                    type="number" min="0" max="100" step="0.5"
                    value={s215?.sbPct ?? 25}
                    onChange={e => updateS215('sbPct', e.target.value)}
                    style={{ width: 60, padding: '5px 7px', borderRadius: 'var(--r-md)', border: '1.5px solid var(--slate-200)', fontSize: 12.5 }}
                  />
                  <span style={{ fontSize: 11, color: 'var(--slate-400)' }}>defaults to statewide 25% (GC §14838)</span>
                </div>
              )}
            </div>

            {/* DVBE Participation — Goal and Incentive are independent */}
            <div className="form-field" style={{ gridColumn: '1 / -1' }}>
              <label>DVBE Participation</label>
              <div style={{ fontSize: 11, color: 'var(--slate-400)', marginBottom: 6 }}>Per the actual STD 816 form, Goal and Incentive can be waived separately or together.</div>
              <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                <div>
                  <div style={{ fontSize: 11, color: 'var(--slate-500)', marginBottom: 3 }}>Goal Requirement</div>
                  <select
                    value={contract.dvbe?.dvbeGoalStatus || ''}
                    onChange={e => updateC({ dvbe: { ...(contract.dvbe || {}), dvbeGoalStatus: e.target.value } })}
                    style={{ width: 190, padding: '8px 10px', borderRadius: 'var(--r-md)', border: '1.5px solid var(--slate-200)', fontSize: 13 }}
                  >
                    <option value="">Select…</option>
                    <option value="applies">Applies</option>
                    <option value="waived">Waived</option>
                    <option value="not_required">Not required</option>
                  </select>
                </div>
                <div>
                  <div style={{ fontSize: 11, color: 'var(--slate-500)', marginBottom: 3 }}>Incentive</div>
                  <select
                    value={contract.dvbe?.dvbeIncentiveStatus || ''}
                    onChange={e => updateC({ dvbe: { ...(contract.dvbe || {}), dvbeIncentiveStatus: e.target.value } })}
                    style={{ width: 190, padding: '8px 10px', borderRadius: 'var(--r-md)', border: '1.5px solid var(--slate-200)', fontSize: 13 }}
                  >
                    <option value="">Select…</option>
                    <option value="applies">Applies</option>
                    <option value="waived">Waived</option>
                    <option value="not_required">Not required</option>
                  </select>
                </div>
              </div>
              {contract.dvbe?.dvbeIncentiveStatus === 'applies' && (
                <div style={{ marginTop: 6, padding: 8, background: 'var(--slate-50)', border: '1px solid var(--slate-200)', borderRadius: 'var(--r-md)' }}>
                  <div style={{ fontSize: 11, color: 'var(--slate-500)', marginBottom: 3 }}>Incentive Structure</div>
                  <select
                    value={contract.dvbe?.dvbeIncentiveStructure || 'standard'}
                    onChange={e => updateC({ dvbe: { ...(contract.dvbe || {}), dvbeIncentiveStructure: e.target.value } })}
                    style={{ width: '100%', maxWidth: 300, padding: '6px 8px', borderRadius: 'var(--r-md)', border: '1.5px solid var(--slate-200)', fontSize: 12 }}
                  >
                    <option value="standard">Standard DGS scale (default)</option>
                    <option value="custom">Custom — specify below</option>
                  </select>
                  {contract.dvbe?.dvbeIncentiveStructure === 'custom' && (
                    <input
                      type="text"
                      value={contract.dvbe?.dvbeIncentiveCustomText || ''}
                      onChange={e => updateC({ dvbe: { ...(contract.dvbe || {}), dvbeIncentiveCustomText: e.target.value } })}
                      placeholder="e.g., flat 5% for all proposed DVBE participation"
                      style={{ width: '100%', marginTop: 5, padding: '6px 8px', borderRadius: 'var(--r-md)', border: '1.5px solid var(--slate-200)', fontSize: 12, boxSizing: 'border-box' }}
                    />
                  )}
                </div>
              )}
              {contract.dvbe?.dvbeGoalStatus === 'applies' && (
                <div style={{ marginTop: 6, display: 'flex', alignItems: 'center', gap: 6 }}>
                  <span style={{ fontSize: 11.5, color: 'var(--slate-500)' }}>Goal %:</span>
                  <input
                    type="number" min="0" max="100" step="0.5"
                    value={s215?.dvbePct ?? 3}
                    onChange={e => updateS215('dvbePct', e.target.value)}
                    style={{ width: 60, padding: '5px 7px', borderRadius: 'var(--r-md)', border: '1.5px solid var(--slate-200)', fontSize: 12.5 }}
                  />
                  <span style={{ fontSize: 11, color: 'var(--slate-400)' }}>defaults to statewide 3% (GC §14838 / MVC §999), STD 215 Field 24</span>
                </div>
              )}
            </div>

            {/* DVBE Waiver */}
            <div className="form-field">
              <label>DVBE Waiver Status</label>
              <select
                value={contract.dvbe?.waiverStatus || ''}
                onChange={e => updateC({ dvbe: { ...(contract.dvbe || {}), waiverStatus: e.target.value } })}
                style={{ width: '100%', padding: '8px 10px', borderRadius: 'var(--r-md)', border: '1.5px solid var(--slate-200)', fontSize: 13 }}
              >
                <option value="">Select…</option>
                <option value="Not Required">Waiver not required</option>
                <option value="Approved">Waiver approved</option>
                <option value="Pending">Waiver pending approval</option>
                <option value="Denied">Waiver denied</option>
              </select>
              {(contract.dvbe?.dvbeGoalStatus === 'waived' || contract.dvbe?.dvbeIncentiveStatus === 'waived') && (
                <div style={{ marginTop: 6, fontSize: 11.5 }}>
                  {contract.dvbe?.std816File
                    ? <span style={{ color: 'var(--green-600)' }}>✓ Signed STD 816 on file ({contract.dvbe.std816File.name})</span>
                    : <span style={{ color: 'var(--orange-600)' }}>⚠ Signed STD 816 not yet uploaded — upload it in the Solicitation Studio (Penny tab) so it's included in the final package.</span>}
                  {' '}
                  <a href="https://www.documents.dgs.ca.gov/dgs/fmc/pdf/std816.pdf" target="_blank" rel="noopener noreferrer" style={{ color: 'var(--slate-500)', fontWeight: 600 }}>
                    Download blank form ↗
                  </a>
                </div>
              )}
            </div>

            {/* Outreach Efforts */}
            <Field
              label="SB/DVBE Outreach Efforts"
              value={contract.dvbe?.outreach || ''}
              onChange={v => updateC({ dvbe: { ...(contract.dvbe || {}), outreach: v } })}
              rows={3}
              className="span-full"
              placeholder="Document outreach efforts — firms contacted, dates, responses received, advertising locations, etc."
            />

            {/* Compliant Firms */}
            <Field
              label="SB/DVBE Firms Participating"
              value={contract.dvbe?.firms || ''}
              onChange={v => updateC({ dvbe: { ...(contract.dvbe || {}), firms: v } })}
              rows={2}
              className="span-full"
              placeholder="List certified SB and/or DVBE firms participating or responding to the solicitation…"
            />

            {/* Notes */}
            <Field
              label="Additional Compliance Notes"
              value={contract.dvbe?.notes || ''}
              onChange={v => updateC({ dvbe: { ...(contract.dvbe || {}), notes: v } })}
              rows={2}
              className="span-full"
              placeholder="Any additional documentation, exceptions, or notes for the procurement file…"
            />
          </div>

          {/* Compliance status indicator */}
          {contract.dvbe?.solMethod && contract.dvbe?.sbGoal && contract.dvbe?.dvbeRequired && (
            <div style={{ marginTop: 12, padding: '10px 14px', borderRadius: 'var(--r-md)', background: 'var(--teal-50)', border: '1.5px solid var(--teal-200)' }}>
              <span style={{ fontWeight: 700, fontSize: 12, color: 'var(--teal-700)' }}>
                ✓ Core SB/DVBE fields documented — ready to advance to Bid Evaluation
              </span>
            </div>
          )}
        </div>
      )}

    </div>
  );
}
